import { t } from "@/shared/lib/th-translations";
import type {
  PerformanceMetric,
  PerformanceProcessBucket,
  PerformanceEmployeeBucket,
} from "@/shared/types/production";

export function metricValue(b: PerformanceEmployeeBucket, metric: PerformanceMetric): number {
  if (metric === "good_units") return b.goodUnits;
  if (metric === "events") return b.events;
  if (metric === "paper_length") return b.paperLength;
  if (metric === "paper_area") return b.paperArea;
  return b.shots;
}

export function formatMetricDisplay(value: number, metric: string): string {
  if (metric === "paper_length") {
    return value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  if (metric === "paper_area") {
    // The stored product is width (mm) × length (m); divide width by 1000
    // to display square metres.
    return (value / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value.toLocaleString();
}

export function metricDisplayLabel(metric: PerformanceMetric): string {
  if (metric === "good_units") {
    return t("production.performance.metric.goodUnits");
  }
  if (metric === "events") {
    return t("production.performance.metric.events");
  }
  if (metric === "shots") {
    return t("production.performance.metric.shots");
  }
  if (metric === "value") {
    return t("production.performance.metric.value");
  }
  if (metric === "paper_length") {
    return t("production.performance.metric.paperLength");
  }
  return t("production.performance.metric.paperArea");
}

export interface ProcessStats {
  processTitle: string;
  count: number;
  mean: number;
  stddev: number;
  min: number;
  max: number;
  percentileByEmployee: Map<string, number>;
  zScoreByEmployee: Map<string, number>;
  valueByEmployee: Map<string, number>;
}

export function computeProcessStats(
  process: PerformanceProcessBucket,
  metric: PerformanceMetric,
): ProcessStats {
  const values = process.buckets.map((b) => metricValue(b, metric));
  const n = values.length;
  const mean = n > 0 ? values.reduce((a, b) => a + b, 0) / n : 0;
  const variance =
    n > 0 ? values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / n : 0;
  const stddev = Math.sqrt(variance);
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0] ?? 0;
  const max = sorted[sorted.length - 1] ?? 0;

  const percentileByEmployee = new Map<string, number>();
  const zScoreByEmployee = new Map<string, number>();
  const valueByEmployee = new Map<string, number>();

  for (const b of process.buckets) {
    const v = metricValue(b, metric);
    valueByEmployee.set(b.employeeId, v);
    const rank = sorted.filter((x) => x < v).length;
    const pct = n > 1 ? (rank / (n - 1)) * 100 : 50;
    percentileByEmployee.set(b.employeeId, pct);
    const z = stddev > 0 ? (v - mean) / stddev : 0;
    zScoreByEmployee.set(b.employeeId, z);
  }

  return {
    processTitle: process.processTitle,
    count: n,
    mean,
    stddev,
    min,
    max,
    percentileByEmployee,
    zScoreByEmployee,
    valueByEmployee,
  };
}

export interface EmployeeAggregate {
  employeeId: string;
  employeeName: string;
  avgPercentile: number;
  processCount: number;
  totalValue: number;
}

export function aggregateEmployees(
  processes: PerformanceProcessBucket[],
  metric: PerformanceMetric,
): EmployeeAggregate[] {
  const statsByProcess = processes.map((p) => computeProcessStats(p, metric));
  const employees = new Map<
    string,
    {
      employeeName: string;
      percentiles: number[];
      total: number;
    }
  >();

  for (let i = 0; i < processes.length; i++) {
    const proc = processes[i];
    const stats = statsByProcess[i];
    if (stats.count < 2) continue; // percentile meaningless for n<2
    for (const b of proc.buckets) {
      const existing = employees.get(b.employeeId) ?? {
        employeeName: b.employeeName,
        percentiles: [],
        total: 0,
      };
      const pct = stats.percentileByEmployee.get(b.employeeId) ?? 50;
      const val = stats.valueByEmployee.get(b.employeeId) ?? 0;
      employees.set(b.employeeId, {
        employeeName: b.employeeName,
        percentiles: [...existing.percentiles, pct],
        total: existing.total + val,
      });
    }
  }

  const aggregates: EmployeeAggregate[] = [];
  for (const [employeeId, entry] of employees.entries()) {
    const avg =
      entry.percentiles.length > 0
        ? entry.percentiles.reduce((a, b) => a + b, 0) / entry.percentiles.length
        : 50;
    aggregates.push({
      employeeId,
      employeeName: entry.employeeName,
      avgPercentile: avg,
      processCount: entry.percentiles.length,
      totalValue: entry.total,
    });
  }

  return aggregates.sort((a, b) => a.avgPercentile - b.avgPercentile);
}

export function percentileToColor(pct: number): string {
  if (pct < 20) return "bg-red-500 text-white";
  if (pct < 40) return "bg-red-300 text-gray-900";
  if (pct < 60) return "bg-yellow-200 text-gray-900";
  if (pct < 80) return "bg-green-200 text-gray-900";
  return "bg-green-500 text-white";
}

export function percentileLabel(pct: number): string {
  return `${Math.round(pct)}%`;
}

const MIN_BASELINE_N = 3;

export function zScoreColor(z: number): string {
  if (z <= -1.5) return "bg-red-500 text-white";
  if (z <= -0.5) return "bg-red-300 text-gray-900";
  if (z < 0.5) return "bg-yellow-200 text-gray-900";
  if (z < 1.5) return "bg-green-200 text-gray-900";
  return "bg-green-500 text-white";
}

export function zScoreBandLabel(z: number): string {
  if (z <= -1.5) return "criticallyLow";
  if (z <= -0.5) return "low";
  if (z < 0.5) return "normal";
  if (z < 1.5) return "high";
  return "criticallyHigh";
}

export function computeZScore(value: number, mean: number, stddev: number): number {
  if (stddev === 0) return 0;
  return (value - mean) / stddev;
}

export function hasEnoughBaseline(count: number): boolean {
  return count >= MIN_BASELINE_N;
}

export { MIN_BASELINE_N };
